import { FormEvent, useEffect, useRef, useState } from "react";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  ProductReplacementReference,
  ReplacementReferenceType,
} from "../../api/dispatchReplacement.api";
import { RootDispatch, RootState } from "../../store";
import {
  fetchReplacementReferences,
  getReplacementReferenceQueryKey,
  resetReplacementMutation,
  submitProductReplacement,
} from "../../store/slices/dispatchReplacementSlice";
import {
  buildProductReplacementPayload,
  ProductReplacementFormValues,
  validateProductReplacement,
} from "./productReplacement.utils";
import { selectStyles } from "../../components/Factory/MaterialSelectAsync";

interface ProductReplacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ReplacementOption {
  value: string;
  label: string;
  reference: ProductReplacementReference;
}

type ReplacementOptionPage = { page: number };

const initialFormValues: ProductReplacementFormValues = {
  serialNumberToReplace: "",
  replacementSerialNumber: "",
  reason: "",
  beneficiaryType: "salesRep",
  beneficiaryId: "",
};

const referenceTypeLabels: Record<ReplacementReferenceType, string> = {
  salesRep: "SalesRep",
  user: "User",
  customer: "Customer Registration",
};

const getReferenceLabel = (
  type: ReplacementReferenceType,
  reference: ProductReplacementReference,
) => {
  if (type === "salesRep") {
    return [reference.name, reference.mobileNumber].filter(Boolean).join(" - ");
  }
  if (type === "user") {
    return [reference.username, reference.role].filter(Boolean).join(" - ");
  }
  return [reference.name || reference.customerName, reference.mobileNumber]
    .filter(Boolean)
    .join(" - ");
};

const ProductReplacementModal = ({
  isOpen,
  onClose,
  onSuccess,
}: ProductReplacementModalProps) => {
  const [values, setValues] =
    useState<ProductReplacementFormValues>(initialFormValues);
  const [selectedOption, setSelectedOption] =
    useState<ReplacementOption | null>(null);
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [referenceSearch, setReferenceSearch] = useState("");
  const dispatch: RootDispatch = useDispatch();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const activeSearchRef = useRef("");
  const activeTypeRef = useRef<ReplacementReferenceType>("salesRep");
  const referenceQueryKey = getReplacementReferenceQueryKey(
    values.beneficiaryType,
    referenceSearch,
  );
  const referenceQuery = useSelector(
    (state: RootState) =>
      state.dispatchReplacement.referenceQueries[referenceQueryKey],
  );
  const isSubmitting = useSelector(
    (state: RootState) =>
      state.dispatchReplacement.submissionStatus === "pending",
  );
  const submissionError = useSelector(
    (state: RootState) => state.dispatchReplacement.submissionError,
  );
  const errorMessage =
    formErrorMessage || submissionError || referenceQuery?.error || "";

  useEffect(() => {
    dispatch(resetReplacementMutation());
    if (isOpen) {
      dialogRef.current?.focus();
      firstInputRef.current?.focus();
      return;
    }

    setValues(initialFormValues);
    setSelectedOption(null);
    setStep("form");
    setFormErrorMessage("");
    setReferenceSearch("");
    activeSearchRef.current = "";
    activeTypeRef.current = "salesRep";
  }, [dispatch, isOpen]);

  const updateValue = <K extends keyof ProductReplacementFormValues>(
    key: K,
    value: ProductReplacementFormValues[K],
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    setFormErrorMessage("");
    dispatch(resetReplacementMutation());
  };

  const loadReferenceOptions: LoadOptions<
    ReplacementOption,
    any,
    ReplacementOptionPage
  > = async (search, loadedOptions, additional) => {
    const page = additional?.page || 1;
    try {
      const type = values.beneficiaryType;
      const response = await dispatch(
        fetchReplacementReferences({ type, search, page, limit: 10 }),
      ).unwrap();

      if (
        search !== activeSearchRef.current ||
        type !== activeTypeRef.current
      ) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const loadedIds = new Set(loadedOptions.map((option) => option.value));
      const options = response.data
        .filter((reference) => reference._id && !loadedIds.has(reference._id))
        .map((reference) => ({
          value: reference._id,
          label:
            getReferenceLabel(values.beneficiaryType, reference) ||
            "Unnamed reference",
          reference,
        }));

      return {
        options,
        hasMore: page < response.pagination.totalPages,
        additional: { page: page + 1 },
      };
    } catch (error) {
      if (
        search === activeSearchRef.current &&
        values.beneficiaryType === activeTypeRef.current
      ) {
        toast.error(
          typeof error === "string"
            ? error
            : "Failed to fetch replacement references.",
        );
      }
      return { options: [], hasMore: false, additional: { page: 1 } };
    }
  };

  const continueToConfirmation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateProductReplacement(values);
    if (validationError) {
      setFormErrorMessage(validationError);
      return;
    }
    setFormErrorMessage("");
    setStep("confirm");
  };

  const submitReplacement = async () => {
    const validationError = validateProductReplacement(values);
    if (validationError) {
      setStep("form");
      setFormErrorMessage(validationError);
      return;
    }

    setFormErrorMessage("");
    try {
      await dispatch(
        submitProductReplacement(buildProductReplacementPayload(values)),
      ).unwrap();
      toast.success("Product replacement recorded successfully.");
      onSuccess();
      onClose();
    } catch (error) {
      setFormErrorMessage(
        typeof error === "string" ? error : "Failed to replace product.",
      );
    }
  };

  if (!isOpen) return null;

  const cancel = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-replacement-title"
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") cancel();
          if (event.key === "Tab") {
            const focusableElements =
              dialogRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
              );
            if (!focusableElements?.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            } else if (
              !event.shiftKey &&
              document.activeElement === lastElement
            ) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }}
        className="my-auto w-full max-w-2xl rounded-2xl bg-white shadow-2xl outline-none"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2
              id="product-replacement-title"
              className="text-lg font-semibold text-slate-800"
            >
              Replace Product
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {step === "form"
                ? "Enter the serials and choose one beneficiary."
                : "Review the replacement details before confirming."}
            </p>
          </div>
          <button
            type="button"
            onClick={cancel}
            disabled={isSubmitting}
            aria-label="Close product replacement"
            className="rounded-md px-2 py-1 text-xl leading-none text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            &times;
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          {step === "form" ? (
            <form onSubmit={continueToConfirmation} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="serial-number-to-replace"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    Serial number being replaced
                  </label>
                  <input
                    ref={firstInputRef}
                    id="serial-number-to-replace"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={10}
                    required
                    value={values.serialNumberToReplace}
                    onChange={(event) =>
                      updateValue("serialNumberToReplace", event.target.value)
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="10-digit serial number"
                  />
                </div>
                <div>
                  <label
                    htmlFor="replacement-serial-number"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    Replacement serial number
                  </label>
                  <input
                    id="replacement-serial-number"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={10}
                    required
                    value={values.replacementSerialNumber}
                    onChange={(event) =>
                      updateValue("replacementSerialNumber", event.target.value)
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="10-digit serial number"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="replacement-reason"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Reason
                </label>
                <textarea
                  id="replacement-reason"
                  required
                  rows={3}
                  value={values.reason}
                  onChange={(event) =>
                    updateValue("reason", event.target.value)
                  }
                  className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Explain why this product is being replaced"
                />
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-700">
                  Beneficiary type
                </legend>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {(
                    Object.keys(
                      referenceTypeLabels,
                    ) as ReplacementReferenceType[]
                  ).map((type) => (
                    <label
                      key={type}
                      className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700"
                    >
                      <input
                        type="radio"
                        name="beneficiary-type"
                        value={type}
                        checked={values.beneficiaryType === type}
                        onChange={() => {
                          updateValue("beneficiaryType", type);
                          updateValue("beneficiaryId", "");
                          setSelectedOption(null);
                          activeSearchRef.current = "";
                          activeTypeRef.current = type;
                          setReferenceSearch("");
                        }}
                        className="accent-blue-600"
                      />
                      {referenceTypeLabels[type]}
                    </label>
                  ))}
                </div>
                <label
                  htmlFor="replacement-beneficiary"
                  className="mb-1 block text-sm font-medium text-slate-700"
                >
                  Search {referenceTypeLabels[values.beneficiaryType]}
                </label>
                <AsyncPaginate
                  key={values.beneficiaryType}
                  inputId="replacement-beneficiary"
                  aria-label={`Search ${referenceTypeLabels[values.beneficiaryType]}`}
                  value={selectedOption}
                  loadOptions={loadReferenceOptions}
                  additional={{ page: 1 }}
                  onInputChange={(inputValue, actionMeta) => {
                    if (actionMeta.action === "input-change") {
                      activeSearchRef.current = inputValue;
                      setReferenceSearch(inputValue);
                    }
                    return inputValue;
                  }}
                  onChange={(option) => {
                    setSelectedOption(option);
                    updateValue("beneficiaryId", option?.value || "");
                  }}
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  placeholder={`Search ${referenceTypeLabels[values.beneficiaryType]}...`}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? "No matching references"
                      : "No references found"
                  }
                  isLoading={referenceQuery?.loading || false}
                  debounceTimeout={300}
                />
              </fieldset>

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={cancel}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Review replacement
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-5">
              <dl className="grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">
                    Old serial number
                  </dt>
                  <dd className="mt-1 break-all text-sm font-medium text-slate-800">
                    {values.serialNumberToReplace.trim()}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">
                    Replacement serial number
                  </dt>
                  <dd className="mt-1 break-all text-sm font-medium text-slate-800">
                    {values.replacementSerialNumber.trim()}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase text-slate-500">
                    Reason
                  </dt>
                  <dd className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                    {values.reason.trim()}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-semibold uppercase text-slate-500">
                    {referenceTypeLabels[values.beneficiaryType]}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-slate-800">
                    {selectedOption?.label}
                  </dd>
                </div>
              </dl>

              <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Confirming records the old unit as returned and dispatches the
                replacement unit.
              </p>

              <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={cancel}
                  disabled={isSubmitting}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStep("form")}
                  disabled={isSubmitting}
                  className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                >
                  Back / Edit
                </button>
                <button
                  type="button"
                  onClick={submitReplacement}
                  disabled={isSubmitting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Confirm replacement"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductReplacementModal;
